import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL, AUTOMATION_SERVICE_URL } from '../config';
import '../styles/BookingAutomation.css';

const CLEAN_API_BASE_URL = (API_BASE_URL || '').replace(/\/+$/, '');

// Vehicle categories and their subtypes derived from fp.trafikverket.se/Boka/ng/licence
// Format used when sending to backend: "Category::Subtype" (e.g. "Personbil::B")
const VEHICLE_CATEGORIES = {
  Motorcykel: ['A', 'A1', 'A2'],
  Personbil: ['B', 'B96', 'BE'],
  Lastbil: ['C', 'C1', 'C1E', 'CE'],
  Buss: ['D', 'D1', 'D1E', 'DE'],
  Yrkestrafiktillstånd: ['Buss', 'Lastbil', 'Taxi'],
  Yrkesförare: ['Buss', 'Lastbil', 'Lokförare', 'Taxi'],
  Moped: ['AM'],
  Traktor: ['Traktor'],
  'Myndigheten för civilt försvar': ['ADR'],
  Certifiering: ['APV', 'VVH'],
};

const CATEGORY_NAMES = Object.keys(VEHICLE_CATEGORIES);

const STATUS_LABELS = {
  created: 'Skapad',
  queued: 'Köad i molnet',
  starting_ec2: 'Startar molninstans (EC2)',
  starting_browser: 'Startar webbläsare',
  loading_booking_page: 'Laddar Trafikverket',
  awaiting_qr_scan: 'Väntar på QR-skanning',
  authenticated: 'Inloggad — bearbetar...',
  navigating_to_licence: 'Navigerar till fordonssidan',
  selecting_vehicle: 'Väljer fordonstyp',
  selecting_start_date: 'Väljer startdatum',
  selecting_test_type: 'Väljer provtyp',
  selecting_locations: 'Väljer orter',
  vehicle_selected: 'Fordonstyp vald ✔',
  qr_timeout: 'Tidsgräns passerad',
  stopped: 'Stoppad',
  failed: 'Misslyckad',
};

const TERMINAL_STATUSES = ['stopped', 'failed', 'completed', 'qr_timeout'];
const RETRYABLE_STATUSES = ['failed', 'qr_timeout'];

const normalizeSessionUpdate = (session) => {
  if (!session) return session;
  if (TERMINAL_STATUSES.includes(session.status)) {
    return { ...session, qr_image_data_url: null };
  }
  return session;
};

const SWEDISH_CITIES = [
  'Alingsås',
  'Ängelholm',
  'Ånge',
  'Arjeplog',
  'Arvidsjaur',
  'Arvika',
  'Avesta',
  'Boden',
  'Bollnäs',
  'Borås',
  'Eksjö',
  'Enköping',
  'Eskilstuna',
  'Fagersta',
  'Falkenberg',
  'Falun',
  'Farsta',
  'Gällivare',
  'Gävle',
  'Gillinge',
  'Gillinge Trafikövningsplats',
  'Gislaved',
  'Göteborg',
  'Halmstad',
  'Hammarstrand',
  'Haparanda',
  'Härnösand',
  'Hässleholm',
  'Hedemora',
  'Helsingborg',
  'Hudiksvall',
  'Järfälla',
  'Järpen',
  'Jokkmokk',
  'Jönköping',
  'Kalix',
  'Kalmar',
  'Karlshamn',
  'Karlshamn / Kalmar',
  'Karlskoga',
  'Karlskrona',
  'Karlstad',
  'Katrineholm',
  'Kinna',
  'Kiruna',
  'Köping',
  'Kramfors',
  'Kristianstad',
  'Kungsbacka',
  'Lidköping',
  'Lindesberg',
  'Linköping',
  'Ljungby',
  'Ljusdal',
  'Ludvika',
  'Luleå',
  'Lund',
  'Lycksele',
  'Malmö',
  'Malung',
  'Mariestad',
  'Mjölby',
  'Mora',
  'Motala',
  'Nässjö',
  'Norrköping',
  'Nyköping',
  'Örebro',
  'Örnsköldsvik',
  'Oskarshamn',
  'Östersund',
  'Övertorneå',
  'Pajala',
  'Piteå',
  'Ronneby',
  'Säffle',
  'Sala',
  'Sandviken',
  'Säve',
  'Skellefteå',
  'Skövde',
  'Söderhamn',
  'Södertälje',
  'Sollefteå',
  'Stockholm',
  'Strömstad',
  'Sundbyberg',
  'Sundsvall',
  'Sunne',
  'Sveg',
  'Täby / Trollhättan',
  'Tranås',
  'Uddevalla',
  'Ulricehamn',
  'Umeå',
  'Upplands Väsby',
  'Uppsala',
  'Vänersborg',
  'Varberg',
  'Värnamo',
  'Vårgårda',
  'Västerås',
  'Västerhaninge',
  'Västerhaninge 2',
  'Västervik',
  'Växjö',
  'Vetlanda',
  'Vilhelmina',
  'Vimmerby',
  'Visby',
  'Ystad',
];

const BookingAutomation = () => {
  const { token, loading } = useAuth();
  const navigate = useNavigate();

  const [vehicleCategory, setVehicleCategory] = useState(CATEGORY_NAMES[0]);
  const [vehicleSubtype, setVehicleSubtype] = useState(VEHICLE_CATEGORIES[CATEGORY_NAMES[0]][0]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [testType, setTestType] = useState('Körprov');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [usageData, setUsageData] = useState(null);
  const [, setUsageLoading] = useState(true);
  const [autoBookEnabled, setAutoBookEnabled] = useState(false);
  const [skipNext30Hours, setSkipNext30Hours] = useState(false);
  const [language, setLanguage] = useState('Svenska');
  const [rentVehicle, setRentVehicle] = useState('Manuell bil');

  const [sessionId, setSessionId] = useState('');
  const [sessionData, setSessionData] = useState(null);
  const activeAutomationUrlRef = useRef('');
  const [uiMessage, setUiMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [settingsChangedPopup, setSettingsChangedPopup] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const eventSourceRef = useRef(null);
  const sessionActiveRef = useRef(false);

  const updateAutomationUrl = useCallback((urlOrIp) => {
    if (!urlOrIp) return;
    let formattedUrl = urlOrIp;
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = `http://${formattedUrl}:5001`;
    }
    activeAutomationUrlRef.current = formattedUrl;
  }, []);

  const fetchUsageData = React.useCallback(async () => {
    if (!token) return;
    try {
      setUsageLoading(true);
      const res = await fetch(`${CLEAN_API_BASE_URL}/booking-automation/usage`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (data.status === 'success') {
        setUsageData(data.usage);
      }
    } catch (err) {
      console.error('Error fetching usage data:', err);
    } finally {
      setUsageLoading(false);
    }
  }, [token]);

  const handleToggleLocation = (city) => {
    if (selectedLocations.includes(city)) {
      setSelectedLocations(selectedLocations.filter((c) => c !== city));
    } else {
      if (selectedLocations.length >= 4) {
        setUiMessage('Du kan max välja 4 orter.');
        setTimeout(() => setUiMessage(''), 3000);
        return;
      }
      setSelectedLocations([...selectedLocations, city]);
    }
  };

  const handleRemoveLocation = (city) => {
    setSelectedLocations(selectedLocations.filter((c) => c !== city));
  };

  useEffect(() => {
    document.title = 'Bokningsassistent - TrafikBevakning';
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!loading && !token) {
      navigate('/login');
    }
  }, [loading, token, navigate]);

  // Refresh usage bar while a session is actively running
  useEffect(() => {
    if (!token || !sessionData) return undefined;
    if (TERMINAL_STATUSES.includes(sessionData.status)) return undefined;

    const interval = setInterval(fetchUsageData, 30000);
    return () => clearInterval(interval);
  }, [token, sessionData, fetchUsageData]);

  useEffect(() => {
    if (token) {
      fetchUsageData();
      setSubscriptionLoading(true);
      fetch(`${CLEAN_API_BASE_URL}/subscription`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.status === 'success') {
            setSubscription(data.subscription);
            if (
              data.subscription?.plan_name !== 'Auto-Bokning' &&
              data.subscription?.plan_name !== 'Auto-Bokning Pro'
            ) {
              setAutoBookEnabled(false);
              setSkipNext30Hours(false);
            }
          }
        })
        .catch((err) => console.error('Error fetching subscription:', err))
        .finally(() => setSubscriptionLoading(false));
    } else {
      setSubscriptionLoading(false);
    }
  }, [token, fetchUsageData]);

  const stopStream = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  const startStream = useCallback(
    (id) => {
      stopStream();
      if (!id || !token) return;

      // Always use auth_server SSE — it polls EC2 and syncs terminal status (failed/qr_timeout) reliably.
      const streamUrl = `${CLEAN_API_BASE_URL}/booking-automation/sessions/${id}/stream?token=${encodeURIComponent(token)}`;
      console.log(`Connecting to session SSE stream via auth_server: ${streamUrl}`);
      const es = new EventSource(streamUrl);
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        try {
          const session = normalizeSessionUpdate(JSON.parse(event.data));
          if (session) {
            setSessionData(session);
            const nodeUrl =
              session?.automation_url ||
              (session?.ec2_public_ip ? `http://${session.ec2_public_ip}:5001` : null);
            if (nodeUrl && nodeUrl !== activeAutomationUrlRef.current) {
              updateAutomationUrl(nodeUrl);
            }

            if (TERMINAL_STATUSES.includes(session?.status)) {
              stopStream();
            }
          }
        } catch (err) {
          console.error('Error parsing SSE event data:', err);
        }
      };

      es.onerror = () => {
        if (eventSourceRef.current === es) {
          stopStream();
        }
      };
    },
    [token, stopStream, updateAutomationUrl]
  );

  // Subscribe to real-time SSE stream when sessionId changes (not when automation URL updates)
  useEffect(() => {
    if (sessionId && token) {
      startStream(sessionId);
    }
    return () => {
      stopStream();
    };
  }, [sessionId, token, startStream, stopStream]);

  const todayStr = new Date().toISOString().split('T')[0];

  const isSwedishMobileNumber = (phone) => {
    if (!phone) return false;
    const clean = phone.replace(/[\s\-()]/g, '');
    const swedishMobileRegex = /^(\+46|0046|0)7[02369]\d{7}$/;
    return swedishMobileRegex.test(clean);
  };

  const authHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const safeJsonParse = async (response) => {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch (e) {
      if (!response.ok) {
        throw new Error(
          `Serverfel (${response.status}): Kunde inte kommunicera med automatiseringsservern.`
        );
      }
      throw new Error('Kunde inte läsa svar från servern.');
    }
  };

  const resetSessionState = useCallback(() => {
    stopStream();
    sessionActiveRef.current = false;
    activeAutomationUrlRef.current = '';
    setSessionId('');
    setSessionData(null);
  }, [stopStream]);

  const startSession = async ({ showOverlay = true, resetFirst = false } = {}) => {
    setSubmitAttempted(true);
    setUiMessage('');

    if (resetFirst) {
      resetSessionState();
    }

    if (!startDate || !endDate) {
      setUiMessage('Välj ett giltigt datumintervall.');
      return;
    }

    if (startDate < todayStr) {
      setUiMessage('Startdatum kan inte vara ett passerat datum.');
      return;
    }

    if (endDate < startDate) {
      setUiMessage('Slutdatum kan inte vara före startdatum.');
      return;
    }

    if (!isSwedishMobileNumber(phoneNumber)) {
      setUiMessage('Ange ett giltigt svenskt mobilnummer (t.ex. 0701234567 eller +46701234567).');
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailAddress || !emailRegex.test(emailAddress)) {
      setUiMessage('Ange en giltig e-postadress (t.ex. namn@example.com).');
      return;
    }

    if (showOverlay) {
      setIsSubmitting(true);
    }

    const sessionPayload = {
      vehicle_type: `${vehicleCategory}::${vehicleSubtype}`,
      start_date: startDate,
      end_date: endDate,
      preferred_location: selectedLocations[0] || '',
      test_type: testType,
      locations: selectedLocations,
      phone_number: phoneNumber,
      email: emailAddress,
      auto_book_enabled: autoBookEnabled,
      skip_next_30_hours: skipNext30Hours,
      language:
        vehicleCategory === 'Personbil' && vehicleSubtype === 'B' && testType === 'Kunskapsprov'
          ? language
          : null,
      rent_vehicle:
        vehicleCategory === 'Personbil' && vehicleSubtype === 'B' && testType === 'Körprov'
          ? rentVehicle
          : null,
    };

    const controller = new AbortController();
    const startTimeoutMs = 60000;
    const timeoutId = setTimeout(() => controller.abort(), startTimeoutMs);

    try {
      let response;
      const fetchOptions = {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(sessionPayload),
        signal: controller.signal,
      };
      try {
        response = await fetch(
          `${CLEAN_API_BASE_URL}/booking-automation/start-session`,
          fetchOptions
        );
      } catch (err) {
        if (err.name === 'AbortError') {
          throw new Error(
            'Start av session tog för lång tid. Kontrollera anslutningen och försök igen.'
          );
        }
        response = await fetch(`${AUTOMATION_SERVICE_URL}/start-session`, {
          ...fetchOptions,
          signal: controller.signal,
        });
      }

      const data = await safeJsonParse(response);
      if (!response.ok) {
        throw new Error(data.error || 'Kunde inte starta automation');
      }

      const id = data.session.session_id;
      const ec2TargetUrl =
        data.session?.automation_url ||
        data.automation_url ||
        (data.session?.ec2_public_ip ? `http://${data.session.ec2_public_ip}:5001` : null);
      if (ec2TargetUrl) {
        updateAutomationUrl(ec2TargetUrl);
      }

      stopStream();
      sessionActiveRef.current = true;
      setSessionId(id);
      setSessionData(normalizeSessionUpdate(data.session));

      fetchUsageData();
    } catch (error) {
      if (error.name === 'AbortError') {
        setUiMessage(
          'Start av session tog för lång tid. Kontrollera anslutningen och försök igen.'
        );
      } else {
        setUiMessage(error.message);
      }
    } finally {
      clearTimeout(timeoutId);
      if (showOverlay) {
        setIsSubmitting(false);
      }
    }
  };

  const handleCreateAndStart = async (event) => {
    event.preventDefault();
    await startSession();
  };

  const handleTryAgain = async () => {
    if (isRetrying || isSubmitting) return;
    setIsRetrying(true);
    setUiMessage('');
    try {
      await startSession({ showOverlay: false, resetFirst: true });
    } finally {
      setIsRetrying(false);
    }
  };

  // Notify user if they change settings while a session is running
  const notifySettingsChange = () => {
    if (
      sessionActiveRef.current &&
      sessionData &&
      !TERMINAL_STATUSES.includes(sessionData?.status)
    ) {
      setSettingsChangedPopup(true);
    }
  };

  const handleStopSession = async () => {
    if (!sessionId) {
      return;
    }

    setIsStopping(true);
    setUiMessage('');
    stopStream();

    try {
      const response = await fetch(
        `${CLEAN_API_BASE_URL}/booking-automation/sessions/${sessionId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await safeJsonParse(response);
      if (!response.ok) {
        throw new Error(data.error || 'Kunde inte stoppa session');
      }

      setUiMessage('Session stoppad. Du kan skapa en ny när du vill.');
      setSessionId('');
      setSessionData(null);
      activeAutomationUrlRef.current = '';
      sessionActiveRef.current = false;
      fetchUsageData();
    } catch (error) {
      setUiMessage(error.message);
    } finally {
      setIsStopping(false);
    }
  };

  const hasActiveSubscription =
    subscription && ['active', 'trialing'].includes(subscription.status);
  const isPremium =
    subscription?.plan_name === 'Auto-Bokning' || subscription?.plan_name === 'Auto-Bokning Pro';

  if (loading || subscriptionLoading) {
    return (
      <div className="booking-loader-screen">
        <div className="booking-loader-inner">
          <img src="/images/favicon_nobg.png" alt="Laddar..." className="booking-loader-logo" />
          <p className="booking-loader-text">Laddar...</p>
        </div>
      </div>
    );
  }

  if (!hasActiveSubscription) {
    return (
      <div className="booking-automation-wrapper">
        <div
          className="booking-automation-container"
          style={{
            textAlign: 'center',
            padding: '50px 20px',
            maxWidth: '600px',
            margin: '40px auto',
          }}
        >
          <div style={{ fontSize: '54px', marginBottom: '16px' }}>🔒</div>
          <h2
            style={{
              fontSize: '1.6rem',
              fontWeight: '700',
              marginBottom: '12px',
              color: '#1a202c',
            }}
          >
            Aktiv prenumeration krävs
          </h2>
          <p
            style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', marginBottom: '28px' }}
          >
            För att komma åt bokningsassistenten och bevaka eller boka lediga provtider behöver du
            en aktiv prenumeration (<strong>Bevakning 99 kr/mån</strong>,{' '}
            <strong>Auto-Bokning 199 kr/mån</strong> eller <strong>Auto-Bokning Pro</strong>).
          </p>
          <button
            type="button"
            onClick={() => navigate('/plans')}
            style={{
              backgroundColor: '#0071e3',
              color: '#ffffff',
              border: 'none',
              padding: '14px 32px',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Välj prenumerationsplan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-automation-wrapper">
      {isSubmitting && (
        <div className="session-starting-overlay" aria-live="polite" aria-busy="true">
          <div className="session-starting-loader">
            <img src="/images/favicon_nobg.png" alt="" className="booking-loader-logo" />
            <p className="booking-loader-text">Startar session...</p>
            <p className="session-starting-subtext">
              Förbereder din bevakningssession. Detta kan ta upp till en minut.
            </p>
          </div>
        </div>
      )}

      <div className="booking-automation-container">
        <div className="booking-automation-header">
          <div className="header-badge">Automatisk Bevakning & Bokning</div>
          <h1>Bokningsassistent</h1>
          <p>
            Konfigurera dina provkriterier nedan. När du startar sessionen hämtar vi QR-koden från
            Trafikverket så att du enkelt kan logga in med din BankID-app.
          </p>
        </div>

        {/* Monthly Quota Usage Banner */}
        <div className="usage-banner-card">
          <div className="usage-banner-header">
            <div>
              <div className="usage-banner-label">
                <svg
                  className="usage-banner-icon"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M8 4.5V8l2.5 1.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Månatlig Bevakningstid ({usageData?.year_month || 'Denna månad'})
              </div>
              <div className="usage-banner-main">
                {usageData?.is_unlimited ? (
                  <>
                    <span>Obegränsad bevakningstid</span>
                    <span className="usage-banner-sub">
                      ({usageData?.used_hours || 0}h använda denna månad • Obegränsad kvot)
                    </span>
                  </>
                ) : (
                  <>
                    {usageData ? `${usageData.remaining_hours} timmar kvar` : 'Laddar...'}
                    <span className="usage-banner-sub">
                      ({usageData?.used_hours || 0}h / {usageData?.max_hours || 300}h använda)
                    </span>
                  </>
                )}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="usage-banner-reset-label">Nollställs den:</div>
              <div className="usage-banner-reset-val">
                {usageData?.reset_date || '1:a nästa månad'} (förs ej över)
              </div>
            </div>
          </div>

          <div className="usage-progress-track">
            <div
              className={`usage-progress-fill ${
                usageData?.is_unlimited
                  ? 'unlimited'
                  : usageData?.quota_exceeded
                    ? 'exceeded'
                    : usageData?.remaining_hours < 20
                      ? 'warning'
                      : ''
              }`}
              style={{
                width: usageData?.is_unlimited
                  ? '100%'
                  : `${Math.min(100, ((usageData?.used_hours || 0) / (usageData?.max_hours || 300)) * 100)}%`,
              }}
            />
          </div>

          {usageData?.quota_exceeded && (
            <div className="usage-alert-box">
              ⚠️ Du har förbrukat din månatliga gräns på 300 timmar. Ny bevakningstid finns
              tillgänglig {usageData.reset_date}.
            </div>
          )}
        </div>

        <form className="booking-form" onSubmit={handleCreateAndStart}>
          {/* Section 1: Fordon & Prov */}
          <div className="form-section">
            <div className="form-section-title">
              <span className="section-step">1</span>
              <div>
                <h2>Fordon & Provtyp</h2>
                <p className="section-subtitle">
                  Välj vilket fordon och prov du vill bevaka eller boka
                </p>
              </div>
            </div>

            <div className="vehicle-pills-row">
              {CATEGORY_NAMES.map((cat) => (
                <button
                  type="button"
                  key={cat}
                  className={`vehicle-pill ${vehicleCategory === cat ? 'active' : ''}`}
                  onClick={() => {
                    setVehicleCategory(cat);
                    setVehicleSubtype(VEHICLE_CATEGORIES[cat][0]);
                    notifySettingsChange();
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="form-grid-2">
              <div className="form-group">
                <label htmlFor="vehicleCategory">Fordonstyp</label>
                <select
                  id="vehicleCategory"
                  value={vehicleCategory}
                  onChange={(e) => {
                    const cat = e.target.value;
                    setVehicleCategory(cat);
                    setVehicleSubtype(VEHICLE_CATEGORIES[cat][0]);
                    notifySettingsChange();
                  }}
                >
                  {CATEGORY_NAMES.map((cat) => (
                    <option value={cat} key={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="vehicleSubtype">Behörighet</label>
                <select
                  id="vehicleSubtype"
                  value={vehicleSubtype}
                  onChange={(e) => {
                    setVehicleSubtype(e.target.value);
                    notifySettingsChange();
                  }}
                >
                  {(VEHICLE_CATEGORIES[vehicleCategory] || []).map((sub) => (
                    <option value={sub} key={sub}>
                      {sub}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-group">
                <label htmlFor="testType">Provtyp</label>
                <select
                  id="testType"
                  value={testType}
                  onChange={(e) => {
                    setTestType(e.target.value);
                    notifySettingsChange();
                  }}
                >
                  <option value="Körprov">Körprov (Körkortsprov)</option>
                  <option value="Kunskapsprov">Kunskapsprov (Teoriprov)</option>
                </select>
              </div>

              {vehicleCategory === 'Personbil' &&
                vehicleSubtype === 'B' &&
                testType === 'Kunskapsprov' && (
                  <div className="form-group">
                    <label htmlFor="language">Språk (Kunskapsprov B)</label>
                    <select
                      id="language"
                      value={language}
                      onChange={(e) => setLanguage(e.target.value)}
                    >
                      <option value="Svenska">Svenska</option>
                      <option value="Albanska">Albanska</option>
                      <option value="Arabiska">Arabiska</option>
                      <option value="BKS">BKS (Bosniska/Kroatiska/Serbiska)</option>
                      <option value="Engelska">Engelska</option>
                      <option value="Finska">Finska</option>
                      <option value="Franska">Franska</option>
                      <option value="Persiska">Persiska</option>
                      <option value="Ryska">Ryska</option>
                      <option value="Somaliska">Somaliska</option>
                      <option value="Sorani">Sorani (Kurdish)</option>
                      <option value="Spanska">Spanska</option>
                      <option value="Thailändska">Thailändska</option>
                      <option value="Tigrinja">Tigrinja</option>
                      <option value="Turkiska">Turkiska</option>
                      <option value="Tyska">Tyska</option>
                    </select>
                  </div>
                )}

              {vehicleCategory === 'Personbil' &&
                vehicleSubtype === 'B' &&
                testType === 'Körprov' && (
                  <div className="form-group">
                    <label htmlFor="rentVehicle">Bilhyra (Körprov B)</label>
                    <select
                      id="rentVehicle"
                      value={rentVehicle}
                      onChange={(e) => setRentVehicle(e.target.value)}
                    >
                      <option value="Manuell bil">Manuell bil</option>
                      <option value="Automatbil">Automatbil</option>
                    </select>
                  </div>
                )}
            </div>
          </div>

          {/* Section 2: Datum & Orter */}
          <div className="form-section">
            <div className="form-section-title">
              <span className="section-step">2</span>
              <div>
                <h2>Datumintervall & Orter</h2>
                <p className="section-subtitle">
                  Välj vilka datum och städer du vill bevaka (max 4 orter)
                </p>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-group">
                <label htmlFor="startDate">Startdatum</label>
                <input
                  id="startDate"
                  type="date"
                  min={todayStr}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                  onInvalid={(e) => e.target.setCustomValidity('Obligatoriskt fält')}
                  onInput={(e) => e.target.setCustomValidity('')}
                  title={!startDate ? 'Obligatoriskt fält' : undefined}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">Slutdatum</label>
                <input
                  id="endDate"
                  type="date"
                  min={startDate || todayStr}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                  onInvalid={(e) => e.target.setCustomValidity('Obligatoriskt fält')}
                  onInput={(e) => e.target.setCustomValidity('')}
                  title={!endDate ? 'Obligatoriskt fält' : undefined}
                />
              </div>
            </div>

            <div className="form-group">
              <div className="label-with-count">
                <label>Önskade Orter</label>
                <span className="location-count-badge">{selectedLocations.length} / 4 valda</span>
              </div>
              <div className="multi-select-container">
                {/* Hidden input to trigger native browser validation bubble for location */}
                <input
                  type="text"
                  required
                  value={selectedLocations.length > 0 ? 'filled' : ''}
                  onChange={() => {}}
                  onInvalid={(e) => e.target.setCustomValidity('Obligatoriskt fält')}
                  onInput={(e) => e.target.setCustomValidity('')}
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                />
                <div
                  className={`selected-tags${submitAttempted && selectedLocations.length === 0 ? ' tags-empty' : ''}`}
                >
                  {selectedLocations.map((city) => (
                    <span key={city} className="location-tag">
                      {city}
                      <button
                        type="button"
                        className="tag-remove"
                        onClick={() => {
                          handleRemoveLocation(city);
                          notifySettingsChange();
                        }}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                  {selectedLocations.length === 0 && (
                    <span className="placeholder-text">
                      Inga orter valda ännu. Välj minst en ort (obligatoriskt).
                    </span>
                  )}
                </div>

                <div className="dropdown-trigger" onClick={() => setDropdownOpen(!dropdownOpen)}>
                  <span>{dropdownOpen ? '▲ Dölj stadslista' : '▼ Välj orter från listan...'}</span>
                </div>

                {dropdownOpen && (
                  <div className="dropdown-menu">
                    <input
                      type="text"
                      className="dropdown-search"
                      placeholder="🔎 Sök ort (t.ex. Stockholm, Göteborg...)"
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="dropdown-list">
                      {SWEDISH_CITIES.filter((city) =>
                        city.toLowerCase().includes(searchFilter.toLowerCase())
                      ).map((city) => {
                        const isSelected = selectedLocations.includes(city);
                        return (
                          <div
                            key={city}
                            className={`dropdown-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleToggleLocation(city)}
                          >
                            <input type="checkbox" checked={isSelected} readOnly />
                            <span>{city}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section 3: Kontakt & Inställningar */}
          <div className="form-section">
            <div className="form-section-title">
              <span className="section-step">3</span>
              <div>
                <h2>Kontakt & Inställningar</h2>
                <p className="section-subtitle">
                  Fyll i kontaktuppgifter för bokningsbekräftelse och välj bokningsläge
                </p>
              </div>
            </div>

            <div className="form-grid-2">
              <div className="form-group">
                <label htmlFor="phoneNumber">Mobilnummer</label>
                <input
                  id="phoneNumber"
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="0701234567"
                  required
                  onInvalid={(e) => e.target.setCustomValidity('Obligatoriskt fält')}
                  onInput={(e) => e.target.setCustomValidity('')}
                  title={!phoneNumber ? 'Obligatoriskt fält' : undefined}
                />
              </div>

              <div className="form-group">
                <label htmlFor="emailAddress">E-postadress</label>
                <input
                  id="emailAddress"
                  type="email"
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  placeholder="namn@example.com"
                  required
                  onInvalid={(e) => e.target.setCustomValidity('Obligatoriskt fält')}
                  onInput={(e) => e.target.setCustomValidity('')}
                  title={!emailAddress ? 'Obligatoriskt fält' : undefined}
                />
              </div>
            </div>

            <div className="booking-mode-header">
              <div className="booking-mode-header-left">
                <span className="booking-mode-label">Bokningsläge</span>
                <p className="booking-mode-desc">
                  Välj hur tjänsten ska agera när en ledig tid hittas
                </p>
              </div>
              {!isPremium && (
                <div className="booking-mode-premium-badge">
                  <span className="premium-badge-text">Kräver premiumplan</span>
                  <button
                    type="button"
                    className="btn-inline-upgrade"
                    onClick={() => navigate('/plans')}
                  >
                    Uppgradera
                  </button>
                </div>
              )}
            </div>

            <div className="settings-box">
              <div className={`checkbox-setting-row${!isPremium ? ' row-disabled' : ''}`}>
                <input
                  type="checkbox"
                  id="autoBookCheckbox"
                  checked={autoBookEnabled && isPremium}
                  onChange={(e) => {
                    if (isPremium) {
                      setAutoBookEnabled(e.target.checked);
                      notifySettingsChange();
                    }
                  }}
                  disabled={!isPremium}
                />
                <div>
                  <label htmlFor="autoBookCheckbox" className="setting-label">
                    Aktivera automatisk bokning
                  </label>
                  <p className="setting-desc">
                    Om du redan har en bokad tid kommer bokningsassistenten automatiskt att boka om
                    dig till den tidigaste tillgängliga tiden.
                  </p>
                </div>
              </div>

              <div className={`checkbox-setting-row${!isPremium ? ' row-disabled' : ''}`}>
                <input
                  type="checkbox"
                  id="skip30hCheckbox"
                  checked={skipNext30Hours && isPremium}
                  onChange={(e) => {
                    if (isPremium) {
                      setSkipNext30Hours(e.target.checked);
                      notifySettingsChange();
                    }
                  }}
                  disabled={!isPremium}
                />
                <div>
                  <label htmlFor="skip30hCheckbox" className="setting-label">
                    Boka ej tider inom 30 kommande timmar
                  </label>
                  <p className="setting-desc">Undviker sista minuten-tider med kort varsel.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="form-submit-row">
            <button
              type="submit"
              className="btn-submit-hero"
              disabled={isSubmitting || usageData?.quota_exceeded}
            >
              {isSubmitting ? (
                <>
                  <span className="btn-spinner" aria-hidden="true" />
                  Startar session...
                </>
              ) : usageData?.quota_exceeded ? (
                'Månatlig gräns nådd (300h)'
              ) : (
                'Skapa och starta session'
              )}
            </button>
          </div>
        </form>

        {uiMessage && <div className="booking-message">{uiMessage}</div>}

        {settingsChangedPopup && (
          <div className="settings-changed-overlay" onClick={() => setSettingsChangedPopup(false)}>
            <div className="settings-changed-popup" onClick={(e) => e.stopPropagation()}>
              <div className="popup-icon">
                <svg
                  width="44"
                  height="44"
                  viewBox="0 0 44 44"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="22" cy="22" r="22" fill="#fff1f2" />
                  <circle cx="22" cy="22" r="18" fill="#ff1616" fillOpacity="0.12" />
                  <circle cx="22" cy="22" r="14" fill="#ff1616" />
                  <rect x="20.5" y="12" width="3" height="13" rx="1.5" fill="white" />
                  <circle cx="22" cy="30.5" r="1.8" fill="white" />
                </svg>
              </div>
              <h3>Inställningar ändrade</h3>
              <p>
                En session pågår redan. Stoppa den och starta en ny session för att tillämpa
                ändringarna.
              </p>
              <button className="popup-dismiss-btn" onClick={() => setSettingsChangedPopup(false)}>
                Förstått
              </button>
            </div>
          </div>
        )}

        {sessionData && (
          <div className="session-panel" key={sessionData.session_id || sessionId}>
            <div className="session-top-row">
              <div className="session-status-badge">
                <span className={`status-dot status-${sessionData.status}`}></span>
                <h2>Aktiv Session</h2>
              </div>
              <span className="session-id">ID: {sessionData.session_id}</span>
            </div>

            <div className="session-details-grid">
              <div className="session-detail-item">
                <span className="detail-label">Status</span>
                <span className="detail-value">
                  {STATUS_LABELS[sessionData.status] || sessionData.status}
                </span>
              </div>
              <div className="session-detail-item">
                <span className="detail-label">Bokningsläge</span>
                <span className="detail-value">
                  {sessionData.auto_book_enabled ? 'Auto-bokning AKTIV' : 'Endast bevakning'}
                </span>
              </div>
              {sessionData.skip_next_30_hours && (
                <div className="session-detail-item">
                  <span className="detail-label">Tidsfilter</span>
                  <span className="detail-value">Undviker nästa 30h</span>
                </div>
              )}
            </div>

            {sessionData.last_message &&
              !['starting_ec2', 'queued'].includes(sessionData.status) &&
              !RETRYABLE_STATUSES.includes(sessionData.status) && (
                <div className="session-info-callout">{sessionData.last_message}</div>
              )}

            {['starting_ec2', 'queued'].includes(sessionData.status) ? (
              <div className="ec2-starting-card">
                <div className="ec2-connection-stage">
                  <div className="connection-visual">
                    <div className="node-box user-node">
                      <div className="node-icon">📱</div>
                      <span className="node-label">Din Enhet</span>
                    </div>
                    <div className="connection-line-container">
                      <div className="animated-pulse-line"></div>
                      <span className="connection-badge">Ansluter</span>
                    </div>
                    <div className="node-box server-node">
                      <div className="node-icon glowing-server">☁️</div>
                      <span className="node-label">Molnserver</span>
                    </div>
                  </div>

                  <div className="ec2-starting-info">
                    <h3 className="ec2-starting-title">Startar Molnserver...</h3>
                    <p className="ec2-starting-desc">
                      Förbereder en dedikerad och isolerad miljö för din bevakningssession. Detta
                      tar vanligtvis cirka 20–60 sekunder.
                    </p>

                    <div className="ec2-starting-progress-bar">
                      <div className="ec2-starting-progress-fill"></div>
                    </div>
                  </div>
                </div>
              </div>
            ) : sessionData.qr_image_data_url && !TERMINAL_STATUSES.includes(sessionData.status) ? (
              <div
                className="qr-card"
                key={`qr-${sessionData.session_id}-${sessionData.qr_revision || 0}`}
              >
                <h3>Skanna med Trafikverket-appen</h3>
                <img src={sessionData.qr_image_data_url} alt="QR-kod för inloggning" />
                <p className="qr-subtext">
                  Öppna appen i din mobil och skanna koden ovan för att slutföra inloggningen.
                </p>
                <span className="qr-meta">
                  Version {sessionData.qr_revision || 0}{' '}
                  {sessionData.qr_updated_at
                    ? `• Uppdaterad ${new Date(sessionData.qr_updated_at).toLocaleTimeString()}`
                    : ''}
                </span>
              </div>
            ) : RETRYABLE_STATUSES.includes(sessionData.status) ? (
              <div className="session-info-callout session-failed-callout">
                <p>{sessionData.last_message || 'Sessionen misslyckades. Försök igen nedan.'}</p>
              </div>
            ) : (
              ['created', 'queued', 'starting_browser', 'loading_booking_page'].includes(
                sessionData.status
              ) && (
                <div className="qr-placeholder">
                  <div className="qr-placeholder-icon">
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 48 48"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="4"
                        y="4"
                        width="16"
                        height="16"
                        rx="2"
                        stroke="#ff1616"
                        strokeWidth="2.5"
                        fill="none"
                      />
                      <rect x="8" y="8" width="8" height="8" rx="1" fill="#ff1616" />
                      <rect
                        x="28"
                        y="4"
                        width="16"
                        height="16"
                        rx="2"
                        stroke="#ff1616"
                        strokeWidth="2.5"
                        fill="none"
                      />
                      <rect x="32" y="8" width="8" height="8" rx="1" fill="#ff1616" />
                      <rect
                        x="4"
                        y="28"
                        width="16"
                        height="16"
                        rx="2"
                        stroke="#ff1616"
                        strokeWidth="2.5"
                        fill="none"
                      />
                      <rect x="8" y="32" width="8" height="8" rx="1" fill="#ff1616" />
                      <rect x="28" y="28" width="4" height="4" rx="0.5" fill="#ff1616" />
                      <rect x="36" y="28" width="8" height="4" rx="0.5" fill="#ff1616" />
                      <rect x="28" y="36" width="8" height="4" rx="0.5" fill="#ff1616" />
                      <rect x="40" y="36" width="4" height="8" rx="0.5" fill="#ff1616" />
                    </svg>
                  </div>
                </div>
              )
            )}

            <div className="session-actions">
              {RETRYABLE_STATUSES.includes(sessionData.status) && (
                <button
                  type="button"
                  className="btn-submit-hero btn-try-again"
                  onClick={handleTryAgain}
                  disabled={isRetrying || isSubmitting || usageData?.quota_exceeded}
                >
                  {isRetrying ? (
                    <>
                      <span className="btn-spinner" aria-hidden="true" />
                      Startar session...
                    </>
                  ) : (
                    'Försök igen'
                  )}
                </button>
              )}
              <button
                type="button"
                className="btn-stop-danger"
                onClick={handleStopSession}
                disabled={isStopping || !sessionId}
              >
                {isStopping ? 'Stoppar och frigör resurser...' : 'Stoppa session'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingAutomation;
